// SearchSection.jsx
import React, { useState, useEffect, useRef } from 'react';
import { LocationIcon, SearchIcons } from '../assets/constants/Icons';
import { useTranslation } from 'react-i18next';

const SearchSection = ({
  searchQuery,
  onSearchChange,
  onSearchClear,
  onSuggestionSelect,
  validData,
  currentLocation,
  filteredLocations,
  setFilteredLocations
}) => {
  const { t } = useTranslation();
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Generate search suggestions
  const generateSearchSuggestions = (query) => {
    if (!query.trim()) return [];
    
    const searchTerm = query.toLowerCase().trim();
    const suggestions = [];
    const seen = new Set();
    
    validData.forEach(loc => {
      const nameMatch = loc.name?.toLowerCase().includes(searchTerm);
      const lockerNumberMatch = loc.lockerNumber?.toLowerCase().includes(searchTerm);
      const cityMatch = loc.city?.toLowerCase().includes(searchTerm);
      const areaMatch = loc.area?.toLowerCase().includes(searchTerm);
      const stateMatch = loc.state?.toLowerCase().includes(searchTerm);
      const countryMatch = loc.country?.toLowerCase().includes(searchTerm);
    
      if (nameMatch && !seen.has(loc.name)) {
        suggestions.push({
          id: loc.id,
          text: loc.name,
          type: 'name',
          location: loc
        });
        seen.add(loc.name);
      }
      if (cityMatch && !seen.has(loc.city)) {
        suggestions.push({
          id: `${loc.id}_city`,
          text: `${loc.name}`,
          type: 'city',
          location: loc
        });
        seen.add(loc.city);
      }
      if(areaMatch && !seen.has(loc.area)) {
        suggestions.push({
          id: `${loc.id}_area`,
          text: `${loc.name}`,
          type: 'area',
          location: loc
        });
        seen.add(loc.area);
      }
        if(stateMatch && !seen.has(loc.state)) {
        suggestions.push({
          id: `${loc.id}_state`,
          text: `${loc.name}`,
          type: 'state',
          location: loc
        });
        seen.add(loc.state);
      }
      if (lockerNumberMatch && !seen.has(loc.lockerNumber)) {
        suggestions.push({
          id: `${loc.id}_locker`,
          text: t('locker_number_label', { lockerNumber: loc.lockerNumber }),
          type: 'locker_number',
          location: loc
        });
        seen.add(loc.lockerNumber);
      }
    });
    
    if (currentLocation && suggestions.length === 0) {
      suggestions.push({
        id: 'No lockers',
        text: t('no_lockers_text'),
        type: 'No lockers',
        location: null
      });
    }
    
    return suggestions.slice(0, 8);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    onSearchChange(value);
    
    if (value.trim()) {
      const suggestions = generateSearchSuggestions(value);
      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
      setHighlightedIndex(-1);
    } else {
      setShowSuggestions(false);
      setSearchSuggestions([]);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'current_location') {
      if (onSuggestionSelect) {
        onSuggestionSelect({
          type: 'current_location',
          location: null
        });
      }
    } else if (suggestion.location) {
      onSearchChange(suggestion.text);
      
      if (onSuggestionSelect) {
        onSuggestionSelect({
          type: 'location',
          location: suggestion.location
        });
      }
      
      // Filter locations based on the search
      const filtered = validData.filter(loc => 
        loc.name === suggestion.location.name || 
        loc.city === suggestion.location.city ||
        loc.id === suggestion.location.id
      );
      if (setFilteredLocations) {
        setFilteredLocations(filtered);
      }
    }
    
    setShowSuggestions(false);
    setSearchSuggestions([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < searchSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && searchSuggestions[highlightedIndex]) {
          handleSuggestionClick(searchSuggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target) &&
          suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get icon for suggestion type
// Get icon for suggestion type (single icon for all)
    const getSuggestionIcon = () => {
      return(
          <LocationIcon size={18} />
      )
    };

  return (
    <div className="search-bar-wrapper">
      <div className="search-bar">
        <span className="search-icon">
            <SearchIcons size={18} />
        </span>
        
        <input
          ref={searchInputRef}
          type="text"
          className="search-input"
          placeholder={t('search_locker_name_placeholder')}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchQuery.trim()) {
              setShowSuggestions(true);
            }
          }}
        />

        {searchQuery && (
          <button
            className="search-clear-btn"
            onClick={() => {
              onSearchChange('');
              setShowSuggestions(false);
              setSearchSuggestions([]);
              if (onSearchClear) {
                onSearchClear();
              }
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && searchSuggestions.length > 0 && (
        <div className="search-suggestions" ref={suggestionsRef}>
          {searchSuggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              className={`suggestion-item ${
                index === highlightedIndex ? 'highlighted' : ''
              }`}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="suggestion-icon">
                {getSuggestionIcon(suggestion.type)}
              </div>

              <div className="suggestion-content">
                <div className="suggestion-text">{suggestion.text}</div>

                {suggestion.type !== 'current_location' &&
                  suggestion.location && (
                    <div className="suggestion-detail">
                      {suggestion.location.distance &&
                        t('km_away_text', { distance: suggestion.location.distance })}
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchSection;
